import React, {useEffect, useMemo} from 'react';
import {View, FlatList, StyleSheet, TouchableOpacity, Image} from 'react-native';
import {Text} from 'react-native-paper';
import Icon from 'react-native-vector-icons/MaterialCommunityIcons';
import {useTranslation} from 'react-i18next';
import {useNavigation, useRoute} from '@react-navigation/native';
import {useMeasurementStore} from '../../store/useMeasurementStore';
import GradientHeader from '../../components/common/GradientHeader';
import {colors, spacing} from '../../theme';

const SelectItemScreen = () => {
  const {t} = useTranslation();
  const navigation = useNavigation();
  const route = useRoute();
  const params = route.params;
  const {catalogItems, fetchCatalogItems} = useMeasurementStore();

  useEffect(() => {
    fetchCatalogItems();
  }, [fetchCatalogItems]);

  const customerGender = params.customerGender;

  const {suggested, others} = useMemo(() => {
    const sugg = [];
    const oth = [];
    catalogItems.forEach(dt => {
      const applicable = dt.gender || 'BOTH';
      if (
        applicable === 'BOTH' ||
        (customerGender === 'MALE' && applicable === 'MALE') ||
        (customerGender === 'FEMALE' && applicable === 'FEMALE')
      ) {
        sugg.push(dt);
      } else {
        oth.push(dt);
      }
    });
    return {suggested: sugg, others: oth};
  }, [catalogItems, customerGender]);

  const handleSelect = dt => {
    navigation.navigate('OrderLineDetail', {
      ...params,
      catalogItemId: dt.id,
      catalogItemName: dt.name,
    });
  };

  const renderItem = ({item}) => (
    <TouchableOpacity onPress={() => handleSelect(item)} activeOpacity={0.7} style={styles.card}>
      {item.mainImageUrl ? (
        <Image source={{uri: item.mainImageUrl}} style={styles.cardImage} />
      ) : (
        <View style={styles.cardImageEmpty}>
          <Icon name="tshirt-crew-outline" size={22} color={colors.primary} />
        </View>
      )}
      <View style={styles.cardInfo}>
        <Text style={styles.itemName}>{item.name}</Text>
        {item.description && (
          <Text style={styles.itemDesc} numberOfLines={1}>{item.description}</Text>
        )}
      </View>
      <Icon name="chevron-right" size={22} color={colors.textLight} />
    </TouchableOpacity>
  );

  return (
    <View style={styles.container}>
      <GradientHeader title="Select Item" onBack={() => navigation.goBack()} />

      <FlatList
        data={[
          ...(suggested.length > 0 ? [{type: 'header', title: 'Suggested Items'}] : []),
          ...suggested.map(d => ({type: 'item', ...d})),
          ...(others.length > 0 ? [{type: 'header', title: 'All Items'}] : []),
          ...others.map(d => ({type: 'item', ...d})),
        ]}
        keyExtractor={(item, idx) => item.type === 'header' ? `h-${idx}` : String(item.id)}
        renderItem={({item}) => {
          if (item.type === 'header') {
            return (
              <Text style={styles.sectionTitle}>{item.title}</Text>
            );
          }
          return renderItem({item});
        }}
        contentContainerStyle={styles.listContent}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Icon name="tshirt-crew-outline" size={48} color={colors.textLight} />
            <Text style={styles.emptyText}>{t('common.noData')}</Text>
          </View>
        }
      />
    </View>
  );
};

const styles = StyleSheet.create({
  container: {flex: 1, backgroundColor: colors.background},
  listContent: {padding: spacing.sm, paddingBottom: spacing.xxl, gap: spacing.sm},
  sectionTitle: {
    fontSize: 13, fontWeight: '600', color: colors.textSecondary,
    textTransform: 'uppercase', letterSpacing: 0.5,
    paddingHorizontal: spacing.sm, paddingTop: spacing.sm,
  },
  card: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: colors.surface,
    borderRadius: 12,
    padding: spacing.md,
    gap: spacing.sm,
    shadowColor: '#000',
    shadowOffset: {width: 0, height: 1},
    shadowOpacity: 0.05,
    shadowRadius: 3,
    elevation: 1,
  },
  cardImage: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.background,
  },
  cardImageEmpty: {
    width: 44,
    height: 44,
    borderRadius: 22,
    backgroundColor: colors.primary + '12',
    justifyContent: 'center',
    alignItems: 'center',
  },
  cardInfo: {flex: 1},
  itemName: {fontSize: 15, fontWeight: '600', color: colors.text},
  itemDesc: {fontSize: 12, color: colors.textSecondary, marginTop: 2},
  emptyContainer: {
    alignItems: 'center',
    paddingTop: spacing.xxl * 2,
  },
  emptyText: {
    color: colors.textSecondary,
    marginTop: spacing.sm,
    fontSize: 14,
  },
});

export default SelectItemScreen;
